import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const fold = (s) => (s || "").toLocaleLowerCase("tr");

export default function SmartCategoryPicker({ categories, value, onChange, placeholder = "Kategori *", allowAll = false, allLabel = "Tümü", tid = "catpick", className = "" }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");

    const byId = useMemo(() => Object.fromEntries((categories || []).map((c) => [c.category_id, c])), [categories]);
    const roots = useMemo(
        () => (categories || []).filter((c) => !c.parent_id).sort((a, b) => a.name.localeCompare(b.name, "tr")),
        [categories]
    );
    const subsOf = (id) => (categories || []).filter((c) => c.parent_id === id).sort((a, b) => a.name.localeCompare(b.name, "tr"));

    const sel = value && value !== "all" ? byId[value] : null;
    const selLabel = sel ? (sel.parent_id && byId[sel.parent_id] ? `${byId[sel.parent_id].name} › ${sel.name}` : sel.name) : null;

    const nq = fold(q).trim();
    const matches = nq
        ? (categories || []).filter((c) => fold(c.name).includes(nq) || (c.parent_id && fold(byId[c.parent_id]?.name).includes(nq)))
        : null;

    const pick = (id) => { onChange(id); setOpen(false); setQ(""); };

    const Row = ({ c, isSub }) => (
        <button
            type="button"
            onClick={() => pick(c.category_id)}
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${isSub ? "pl-7 text-muted-foreground" : "font-semibold"}`}
            data-testid={`${tid}-option-${c.category_id}`}
        >
            {isSub && <span className="text-muted-foreground/60">↳</span>}
            <span className="flex-1 truncate">{isSub ? c.name : c.name}</span>
            {value === c.category_id && <Check className="h-4 w-4 shrink-0 text-primary" />}
        </button>
    );

    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
            <PopoverTrigger asChild>
                <button type="button" className={`flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 text-sm transition-colors hover:border-terra-500/50 ${className}`} data-testid={tid}>
                    <span className={`truncate ${selLabel ? "" : "text-muted-foreground"}`}>{selLabel || (allowAll && value === "all" ? allLabel : placeholder)}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-72 p-2" data-testid={`${tid}-dropdown`}>
                <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Kategori ara... (ana veya alt)"
                        className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-sm outline-none focus:border-terra-500"
                        data-testid={`${tid}-search`}
                    />
                </div>
                <div className="max-h-72 overflow-y-auto">
                    {allowAll && !nq && (
                        <button type="button" onClick={() => pick("all")} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors hover:bg-muted" data-testid={`${tid}-all`}>
                            {allLabel}
                            {value === "all" && <Check className="h-4 w-4 text-primary" />}
                        </button>
                    )}
                    {matches ? (
                        matches.length ? (
                            matches.map((c) => (
                                <button
                                    key={c.category_id}
                                    type="button"
                                    onClick={() => pick(c.category_id)}
                                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
                                    data-testid={`${tid}-option-${c.category_id}`}
                                >
                                    <span className="flex-1 truncate">
                                        {c.parent_id && byId[c.parent_id] && <span className="text-muted-foreground">{byId[c.parent_id].name} › </span>}
                                        {c.name}
                                    </span>
                                    {value === c.category_id && <Check className="h-4 w-4 shrink-0 text-primary" />}
                                </button>
                            ))
                        ) : (
                            <p className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid={`${tid}-empty`}>"{q}" ile eşleşen kategori yok</p>
                        )
                    ) : (
                        roots.map((r) => (
                            <div key={r.category_id} data-testid={`${tid}-group-${r.category_id}`}>
                                <Row c={r} />
                                {subsOf(r.category_id).map((s) => <Row key={s.category_id} c={s} isSub />)}
                            </div>
                        ))
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
